import { useState, useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'archipro:visited-website:';

/**
 * Tracks whether the user has previously clicked a "Visit Website" link
 * for a specific page. Persists to localStorage so the tick indicator
 * survives page navigation and browser sessions.
 *
 * Keyed by `pageID` (the library item / siteTreeID) so that different
 * products from the same supplier are tracked independently, even if they
 * share the same external website URL.
 *
 * @param pageID The unique ID of the page (product, professional, project,
 *   article). Pass `undefined` if not yet known (the hook will be inert).
 * @returns `hasVisited` — whether the link has been clicked before.
 * @returns `markVisited` — call on click to persist the visited state.
 */
export function useWebsiteVisited(pageID: number | undefined) {
    const [hasVisited, setHasVisited] = useState<boolean>(() => {
        if (pageID === undefined || typeof window === 'undefined') {
            return false;
        }
        const key = STORAGE_KEY_PREFIX + String(pageID);
        try {
            return localStorage.getItem(key) === '1';
        } catch {
            return false;
        }
    });

    const markVisited = useCallback(() => {
        if (pageID === undefined || typeof window === 'undefined') {
            return;
        }
        const key = STORAGE_KEY_PREFIX + String(pageID);
        try {
            localStorage.setItem(key, '1');
        } catch {
            // localStorage may be full or unavailable — silently ignore.
        }
        setHasVisited(true);
    }, [pageID]);

    return { hasVisited, markVisited } as const;
}
